import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextArea } from '../../../core/textarea';
import type { ContentDisplayVariant } from '../../hooks/content-renderer-types';
import type { StudentExerciseContent } from '../../hooks/use-class-blocks';
import { ExerciseType } from '@org/model';
import type {
  ExerciseFeedbackMessage,
  ExerciseSubmitHandler,
} from '../../hooks/content-renderer-types';
import { ContentDescription } from '../shared/content-description';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { useDraft } from '../../hooks/use-draft';
import { WorkbookPanel, ZineButton } from '../../zine';
import { zineColors } from '../../zine/zine-theme';
import {
  splitWorkbookFeedback,
  WorkbookAttemptText,
  WorkbookExtraFeedback,
  WorkbookVerdict,
} from './zine-workbook';

interface WrittenExerciseProps {
  content: StudentExerciseContent;
  onExerciseSubmit?: ExerciseSubmitHandler;
  feedback?: ExerciseFeedbackMessage[];
  variant?: ContentDisplayVariant;
  isReadOnly?: boolean;
}

export function WrittenExercise({
  content,
  onExerciseSubmit,
  feedback,
  variant = 'card',
  isReadOnly = false,
}: WrittenExerciseProps) {
  void variant; // the workbook panel is the single presentation for this exercise
  const { t } = useTranslation('p2e');
  const initialAnswer = useMemo(() => {
    const submission = content.lastSubmission;
    if (
      submission?.type === ExerciseType.LongAnswer ||
      submission?.type === ExerciseType.ShortAnswer
    ) {
      return submission.answer ?? '';
    }
    return '';
  }, [content.lastSubmission]);

  const { value: answer, setValue: setAnswer, clear: clearDraft } = useDraft<string>(
    ['written', content.exerciseId, content.id],
    initialAnswer,
    { prefix: 'p2e-exercise-draft' },
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!onExerciseSubmit) {
      return;
    }

    try {
      setIsSubmitting(true);
      await onExerciseSubmit(content, {
        type:
          content.exerciseSubtype === ExerciseType.LongAnswer
            ? ExerciseType.LongAnswer
            : ExerciseType.ShortAnswer,
        answer,
      });
      clearDraft();
    } finally {
      setIsSubmitting(false);
    }
  };

  const { verdictMessage, extraMessages } = splitWorkbookFeedback(feedback);

  return (
    <form onSubmit={handleSubmit}>
      <WorkbookPanel
        tab={t('zine.workbook.tab.write')}
        title={
          content.title ? renderInlineMarkdown(content.title, 'written-exercise-title') : undefined
        }
        hint={t('zine.workbook.writePrompt')}
        footer={
          <>
            {onExerciseSubmit && !isReadOnly && (
              <ZineButton type="submit" isLoading={isSubmitting} disabled={!answer.trim() || isSubmitting}>
                {isSubmitting ? t('zine.workbook.submitting') : t('zine.workbook.submitAnswer')}
              </ZineButton>
            )}
            {!isReadOnly && <WorkbookAttemptText content={content} />}
            <WorkbookVerdict pending={isSubmitting} message={verdictMessage} />
            <WorkbookExtraFeedback pending={isSubmitting} messages={extraMessages} />
          </>
        }
      >
        <ContentDescription text={content.description} markdown className="mb-3" />
        <div className="mb-4 space-y-1.5">
          <label
            htmlFor={`${content.id}-answer`}
            className="text-[.78rem] font-semibold"
            style={{ color: zineColors.warmGray }}
          >
            {t('zine.workbook.answerLabel')}
          </label>
          <TextArea
            id={`${content.id}-answer`}
            value={answer}
            onChange={(event) => {
              if (isReadOnly) {
                return;
              }
              setAnswer(event.target.value);
              setLastSavedAt(Date.now());
            }}
            placeholder={t('zine.workbook.writePlaceholder')}
            className="min-h-[120px] rounded-[4px] border-[1.5px] bg-white text-[.95rem] shadow-none"
            style={{ borderColor: zineColors.line, color: zineColors.ink }}
            readOnly={isReadOnly}
          />
          {!isReadOnly && lastSavedAt ? (
            <p className="m-0 text-xs" style={{ color: zineColors.warmGray }}>
              {t('zine.workbook.draftSaved')}
            </p>
          ) : null}
        </div>
      </WorkbookPanel>
    </form>
  );
}
